import { ExclamationCircle, Left } from "@repo/icons";
import { z } from "zod";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { type FragmentType, graphql } from "@repo/graphql-types";
import { HeaderButton, HeaderRoot, HeaderTitle } from "@/components/header";
import { Page } from "@/components/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import {
  RefundReceiptDetails,
  type RefundReceiptDetailsQueryFragment,
} from "@/components/refund/refund-receipt-details";
import {
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/empty-state";

const RefundReceiptPageQuery = graphql(`
  query RefundReceiptPageQuery($codAgendamento: Int, $onlinePaymentGuid: String) {
    ExpensumGetRefundReceipt(
      arg1: {codAgendamento: $codAgendamento, onlinePaymentGuid: $onlinePaymentGuid}
    ) {
      receipt{
        transactionId
      }
      ...RefundReceiptDetailsQueryFragment
    }
  }
`);

const RefundReceiptPageSearchSchema = z.object({
  guid: z.string().optional(),
  appointmentId: z.number().optional(),
  action: z.string().optional(),
});

export const RefundReceiptPage = (): JSX.Element => {
  const { guid, appointmentId } = useSearch({
    from: "/settings/payments/patient-payments/refund-receipt",
  });

  const queryResult = useGraphQL(RefundReceiptPageQuery, {
    onlinePaymentGuid: guid,
    codAgendamento: appointmentId,
  });

  const { data } = queryResult;

  const router = useRouter();

  const handleBack = (): void => {
    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" onClick={handleBack} />
        <HeaderTitle title="Comprovante" align="center" />
      </HeaderRoot>
      <Page className="p-2">
        <AsyncDataWrapper {...queryResult}>
          {data?.ExpensumGetRefundReceipt?.receipt?.transactionId ? (
            <RefundReceiptDetails
              data={
                data.ExpensumGetRefundReceipt as FragmentType<
                  typeof RefundReceiptDetailsQueryFragment
                >
              }
            />
          ) : (
            <EmptyStateRoot>
              <EmptyStateIcon icon={ExclamationCircle} fill="primary" />
              <EmptyStateTitle>Comprovante em processamento</EmptyStateTitle>
              <EmptyStateDescription>
                O comprovante de estorno está sendo gerado. Em alguns instantes, acesse
                novamente para visualizar e compartilhar.
              </EmptyStateDescription>
            </EmptyStateRoot>
          )}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/payments/patient-payments/refund-receipt",
)({
  component: RefundReceiptPage,
  validateSearch: RefundReceiptPageSearchSchema,
});
